import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { omitBy, isNil, isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { useQuery } from "Hooks/index";
import useSession from "Hooks/session.hook";
import usePagination from "Hooks/pagination.hook";
import { toaster, toNum } from "Helpers/index";
import { SortingMode } from "../../../types/common.types";
import { Tabs } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import {
  TrainReportList,
  BoxesReportList,
  TrolleyReportList,
  ParcelReportList,
  TripReportList,
} from "Components/delivery-management/report-tabs";
import {
  listTrainReports,
  listTrolleyReports,
  listParcelsReports,
  listBoxReports,
  listTripReports,
  listTripDetails,
} from "Services/delivery-report.services";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { useRecoilState } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import useCorporateType from "Hooks/corporate-type.hook";
import {
  parcelModel,
  boxModel,
  trolleyModel,
  trainModel,
  handleProcessTrainResponse,
} from "./delivery-report-excel-model";
import useCheckUserRole from "Hooks/check-user-role.hook";
import { UserRoleToAssign } from "Types/user.types";

const DeliveryReportPage: React.FC = () => {
  const query = useQuery();
  const { t } = useTranslation();
  const history = useHistory();
  const { roles, org } = useSession();
  const { isCustomer } = useCorporateType();
  const { isRole } = useCheckUserRole();
  const [globalFilterState] = useRecoilState(GlobalFilterAtom);
  const initialFilters = {
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
    onlyLatestStatus: isCustomer ? true : null,
  };
  const tabKey = query.get("tabKey") || "0";
  const [currentListCount, setCurrentListCount] = useState(0);
  const [currentPath, setCurrentPath] = useState<any>(
    "/delivery-management/report",
  );
  const { paginationComponent, itemPerPage, queryParams } = usePagination({
    currentListCount,
    currentPath,
  });
  const [filterObj, setFilterObj] = useState<any>(initialFilters);
  const [reportListing, setReportListing] = useState<any>([]);
  const [tripDetails, setTripDetails] = useState<{ [key: string]: any[] }>({});
  const [isLoadingTripDetails, setIsLoadingTripDetails] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExcelLoading, setIsExcelLoading] = useState<boolean>(false);
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);

  const handleTabClick = (key) => {
    const path = `/delivery-management/report?tabKey=${key}`;

    setFilterObj(initialFilters);
    setReportListing([]);
    setCurrentPath(path);
    setTabActiveKey(key);
    history.push(path);
  };

  const onFilter = (filterInput) => {
    setFilterObj(
      isEmpty(filterInput)
        ? initialFilters
        : {
            ...filterObj,
            fromDate: filterInput?.dateFrom || null,
            toDate: filterInput?.dateTo || null,
            status: filterInput?.status || null,
            companyOrgID: filterInput?.companyOrgID || org._id,
            orFields: filterInput?.orFields || null,
            sortByCreatedAt: filterInput?.sortByCreatedAt || SortingMode.ASC,
            onlyLatestStatus:
              filterInput?.onlyLatestStatus == "true" ? true : null,
          },
    );
  };

  const handleListingParcelReport = async (
    enforceLimit = true,
    isWeb = true,
  ) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);

    let filters: any = omitBy({ ...filterObj, sortByCreatedAt: null }, isNil);

    const responseData = await listParcelsReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );

    if (responseData && !responseData.error) {
      // if this is for Excel, do not save the data in the state
      if (isWeb) {
        setReportListing(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", "Failed to fetch orders. Please refresh the page.");
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false); // do not remove this, need to execute if failed response
  };

  const handleListingBoxReport = async (enforceLimit = true, isWeb = true) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);

    let filters: any = omitBy({ ...filterObj, sortByCreatedAt: null }, isNil);
    const responseData = await listBoxReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );
    if (responseData && !responseData.error) {
      // if this is for Excel, do not save the data in the state
      if (isWeb) {
        setReportListing(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", "Failed to fetch orders. Please refresh the page.");
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleListingTrolleyReport = async (
    enforceLimit = true,
    isWeb = true,
  ) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);

    let filters: any = omitBy({ ...filterObj, sortByCreatedAt: null }, isNil);
    const responseData = await listTrolleyReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );
    if (responseData && !responseData.error) {
      // if this is for Excel, do not save the data in the state
      if (isWeb) {
        setReportListing(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", "Failed to fetch orders. Please refresh the page.");
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleListingTrainReport = async (
    enforceLimit = true,
    isWeb = true,
  ) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);

    let filters: any = omitBy({ ...filterObj, sortByCreatedAt: null }, isNil);

    const responseData = await listTrainReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );

    if (responseData && !responseData.error) {
      if (isWeb) {
        setReportListing(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", "Failed to fetch orders. Please refresh the page.");
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleListingTripReport = async (enforceLimit = true, isWeb = true) => {
    isWeb
      ? setIsLoading && setIsLoading(true)
      : setIsExcelLoading && setIsExcelLoading(true);

    let filters: any = omitBy(
      {
        ...filterObj,
        sortByCreatedAt: null,
        companyOrgID: null,
        orgID: org._id,
      },
      isNil,
    );

    const responseData = await listTripReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );

    if (responseData && !responseData.error) {
      if (isWeb) {
        setReportListing(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", responseData.error?.message);
    }

    setIsLoading && setIsLoading(false);
    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleListingTripDetails = async (tripID: string) => {
    setIsLoadingTripDetails((prev) => ({ ...prev, [tripID]: true }));

    let filters: any = omitBy(
      {
        ...filterObj,
        sortByCreatedAt: null,
        companyOrgID: null,
        orgID: org._id,
        tripReportID: tripID,
      },
      isNil,
    );

    const responseData = await listTripDetails(
      filters,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "createdAt", mode: filterObj?.sortByCreatedAt },
    );

    if (responseData && !responseData.error) {
      setTripDetails((prev: any) => ({
        ...prev,
        [tripID]: responseData,
      }));
    } else {
      toaster("error", responseData.error?.message);
    }

    setIsLoadingTripDetails((prev) => ({ ...prev, [tripID]: false }));
  };

  const handleAPICalls = () => {
    // handleListing based on userRoles
    // api is called based on tabKey so we need to differentiate it
    type ReportHandler = () => void;

    // we can create common handlers if multiple user roles have same tabKey api calling
    const sharedHandlers: Record<string, ReportHandler> = {
      "0": handleListingParcelReport,
      "1": handleListingBoxReport,
      "2": handleListingTrolleyReport,
      "3": handleListingTrainReport,
      "4": handleListingTripReport,
      default: handleListingParcelReport,
    };

    // role based handler
    const roleBasedHandlers: Record<string, Record<string, ReportHandler>> = {
      [UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER]: {
        "0": handleListingTrolleyReport,
        "1": handleListingTrainReport,
        default: handleListingTrolleyReport,
      },
      [UserRoleToAssign.SUPER_ADMIN]: sharedHandlers,
      default: sharedHandlers,
      // add more user roles here
    };

    const role = (roles[0]?.subType as UserRoleToAssign) || "default";
    const handlers = roleBasedHandlers[role] || roleBasedHandlers.default;

    // Use the handler for the active tab key, or fallback to the default handler
    const handler = handlers[tabActiveKey] || handlers.default;

    if (handler) {
      handler();
    } else {
      console.error(
        `No handler defined for role: ${role}, tab: ${tabActiveKey}`,
      );
    }
  };

  const handleDownloadExcelFile = async (
    tableData: any[],
    excelType: string,
    sheetName?: string,
  ) => {
    let fileName;
    let allData;

    try {
      // need to get every data
      switch (excelType) {
        case "parcel":
          allData = await handleListingParcelReport(false, false);
          break;
        case "boxes":
          allData = await handleListingBoxReport(false, false);
          break;
        case "trolley":
          allData = await handleListingTrolleyReport(false, false);
          break;
        case "train":
          allData = await handleListingTrainReport(false, false);
          break;
        default:
      }

      // for the train data, there is an additional processing to do
      switch (excelType) {
        case "train":
          allData = handleProcessTrainResponse(allData);
          break;
        default:
      }

      const mappedSheet = allData?.map((parentData, index) => {
        let reportInfo;

        switch (excelType) {
          case "parcel":
            fileName = "Parcel Report Listing";
            reportInfo = parcelModel(parentData, index + 1);
            break;
          case "boxes":
            fileName = "Boxes Report Listing";
            reportInfo = boxModel(parentData, index + 1);
            break;
          case "trolley":
            fileName = "Trolley Report Listing";
            reportInfo = trolleyModel(parentData, index + 1);
            break;
          case "train":
            fileName = "Train Report Listing";
            reportInfo = trainModel(parentData, index + 1);
          default:
        }

        return reportInfo;
      });

      const worksheet = XLSX.utils.json_to_sheet(mappedSheet);

      // Styling is only supported on SheetJS Pro :(

      // // Define the wrap text style
      // const wrapTextStyle = {
      //   alignment: { wrapText: true },
      // };

      // // Apply the wrap text style to column M to P
      // const range = XLSX.utils.decode_range(worksheet["!ref"]!);
      // for (let C = 12; C <= 15; ++C) {
      //   // Columns M (index 12) to P (index 15)
      //   for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      //     const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
      //     if (!worksheet[cellAddress]) continue;

      //     worksheet[cellAddress].s = wrapTextStyle;
      //   }
      // }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.ms-excel",
      });
      saveAs(fileData, `${fileName}.xlsx`);
    } catch (e) {
      console.log("Failed to generate report", e);
      toaster("error", t("error.failedGenerateReport"));
    }
  };

  const filterTabs = () => {
    // tabs for customer
    const defaultTabs = [
      {
        tabKey: "0",
        label: t("tab.parcels"),
        component: <ParcelReportList {...listProps} />,
      },
    ];

    const expandedTabs = [
      ...defaultTabs,
      {
        tabKey: "1",
        label: t("tab.boxes"),
        component: <BoxesReportList {...listProps} />,
      },
      {
        tabKey: "2",
        label: t("tab.trolley"),
        component: <TrolleyReportList {...listProps} />,
      },
      {
        tabKey: "3",
        label: t("tab.train"),
        component: <TrainReportList {...listProps} />,
      },
      {
        tabKey: "4",
        label: t("tab.trip"),
        component: (
          <TripReportList
            isLoadingTripDetails={isLoadingTripDetails}
            tripDetails={tripDetails}
            handleListingTripDetails={handleListingTripDetails}
            {...listProps}
          />
        ),
      },
    ];

    const publicTransportRoleTabs = [
      {
        tabKey: "2",
        label: t("tab.trolley"),
        component: <TrolleyReportList {...listProps} />,
      },
      {
        tabKey: "3",
        label: t("tab.train"),
        component: <TrainReportList {...listProps} />,
      },
    ];

    if (isCustomer) return defaultTabs;

    if (isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER]))
      return publicTransportRoleTabs;
    return expandedTabs;
  };

  const listProps = {
    onFilter: onFilter,
    onDropdownFilter: onFilter,
    reportListing: reportListing,
    handleDownloadExcelFile: handleDownloadExcelFile,
    itemPerPage: itemPerPage,
    isLoading: isLoading,
    handleRefreshList: handleAPICalls,
    isExcelLoading: isExcelLoading,
    disableShowLatestStatus: isCustomer,
  };

  useEffect(() => {
    // prevent calling with empty orgid; other users may be able to see reports
    if (filterObj.companyOrgID) handleAPICalls();
  }, [filterObj, queryParams, itemPerPage]);

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      setFilterObj({
        ...filterObj,
        ancestorOrgIDs: globalFilterState?.selectedOrgIDs,
      });
    }
  }, [globalFilterState]);

  return (
    <div>
      <Helmet>
        <title>{t("title.deliveryManagement")}</title>
      </Helmet>
      <ModuleTitleBar titleText={t("title.deliveryManagement")} />

      <Tabs
        destroyInactiveTabPane={true}
        onTabClick={handleTabClick}
        defaultActiveKey="0"
        activeKey={tabActiveKey}
        tabBarGutter={25}
        tabs={filterTabs()}
      />
      <ErrorBoundary>
        <div className="my-30">{paginationComponent}</div>
      </ErrorBoundary>
    </div>
  );
};

export default DeliveryReportPage;
